import React, { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import ErrorMessage from '@components/molecules/ErrorMessage';
import ClientViewBanner from '@components/molecules/ClientViewBanner';
import { Button } from '@components/atoms/Button';
import { Loading } from '@components/atoms/Loading';
import { useMyClientAccess } from '@hooks/api/clients/useMyClientAccess';
import { useProjects } from '@hooks/api/projects';
import { useClientProjectsOverview } from '@hooks/api/clients/useClientProjectsOverview';
import { getClientVisibleTabs } from '@pages/projects/clientTabs';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { useTranslation } from '@hooks/useTranslation';
import { isFeatureEnvEnabled } from '@utils/featureFlag';
import { DashboardHeader } from '../components/DashboardHeader';
import { ClientFeedColumn } from './ClientFeedColumn';
import { ClientAttentionKind } from './ClientAttentionBlock';
import { ClientProjectsRail } from './ClientProjectsRail';
import { deriveClientDashboardFlags } from './clientDashboardFlags';
import { sortClientProjects } from './clientProjectDisplay';

/**
 * Sprint 43, story-223. The CLIENT's home at `/dashboard`. Reads the CLIENT's own checklist once
 * (`myClientAccess`) and renders only what is switched ON; nothing checklist-dependent renders
 * until it resolves, and a failure shows an error with retry (never a guessed layout). The
 * server already returns empty for every section whose key is OFF, so this gate is a courtesy,
 * never the enforcement.
 *
 * Never renders: star toggle, geofence control, "Needs your action", the Warehouse and Bids
 * pills, acknowledge actions, the trial banner, or a create-project empty state.
 */
const ClientDashboardPage: React.FC = () => {
  const tDashboard = useTranslation('dashboard');
  const history = useHistory();
  const fallbackOrgName = useAppSelector(
    (state: RootState) => state.organization.name,
  );
  const {
    data: access,
    isSuccess: accessReady,
    isError: accessError,
    isLoading: accessLoading,
    refetch: refetchAccess,
  } = useMyClientAccess();
  const {
    data: projects,
    isLoading: projectsLoading,
    isError: projectsError,
    refetch: refetchProjects,
  } = useProjects();

  const flags = useMemo(
    () =>
      deriveClientDashboardFlags(
        access?.settings,
        isFeatureEnvEnabled('INVOICES'),
      ),
    [access],
  );
  const projectIds = useMemo(
    () =>
      (projects ?? [])
        .map((project) => project.projectId)
        .filter((id): id is string => !!id),
    [projects],
  );
  const {
    data: overviews,
    isLoading: overviewsLoading,
    isError: overviewsError,
  } = useClientProjectsOverview(projectIds);
  const sortedProjects = useMemo(
    () => sortClientProjects(projects ?? [], overviews),
    [projects, overviews],
  );
  const visibleTabs = useMemo(
    () => getClientVisibleTabs(access?.settings),
    [access],
  );
  const organizationName = access?.organizationName || fallbackOrgName || '';

  const handleProjectClick = useCallback(
    (projectId: string) => history.push(`/projects/${projectId}`),
    [history],
  );
  // The attention row opens the matching tab only when that tab exists for this client.
  const handleAttentionNavigate = useCallback(
    (projectId: string, kind: ClientAttentionKind) => {
      const tab = kind === 'awaitingApproval' ? 'tasks' : 'incidents';
      history.push(
        visibleTabs.includes(tab)
          ? `/projects/${projectId}?tab=${tab}`
          : `/projects/${projectId}`,
      );
    },
    [history, visibleTabs],
  );
  const handleRetry = useCallback(() => {
    refetchAccess();
    refetchProjects();
  }, [refetchAccess, refetchProjects]);

  const header = <DashboardHeader title={organizationName || undefined} />;

  if (accessError || projectsError) {
    return (
      <div className="flex flex-col gap-3 w-full">
        {header}
        <ErrorMessage message={tDashboard.clientLoadError} />
        <div>
          <Button variant="tertiary" onClick={handleRetry}>
            {tDashboard.clientRetry}
          </Button>
        </div>
      </div>
    );
  }

  if (!accessReady || accessLoading) {
    return (
      <div className="flex flex-col gap-3 w-full">
        {header}
        <div className="flex justify-center py-12">
          <Loading size="xl" />
        </div>
      </div>
    );
  }

  const hasProjects = sortedProjects.length > 0;
  const showFeedColumn = flags.showFeed && (projectsLoading || hasProjects);

  return (
    <div className="flex flex-col gap-3 w-full">
      <ClientViewBanner orgName={organizationName} />

      {showFeedColumn ? (
        <div className="grid grid-cols-1 md:grid-cols-[60%_40%] w-full gap-3">
          {projectsLoading ? (
            <div className="flex flex-col gap-3 min-h-0">{header}</div>
          ) : (
            <ClientFeedColumn
              projects={sortedProjects}
              flags={flags}
              onProjectClick={handleProjectClick}
              headerSlot={header}
            />
          )}
          <ClientProjectsRail
            projects={sortedProjects}
            isLoading={projectsLoading}
            layout="rail"
            organizationName={organizationName}
            onProjectClick={handleProjectClick}
            overviews={overviews}
            overviewsLoading={overviewsLoading}
            overviewsError={overviewsError}
            onAttentionNavigate={handleAttentionNavigate}
          />
        </div>
      ) : (
        <>
          {header}
          <ClientProjectsRail
            projects={sortedProjects}
            isLoading={projectsLoading}
            layout="grid"
            organizationName={organizationName}
            onProjectClick={handleProjectClick}
            overviews={overviews}
            overviewsLoading={overviewsLoading}
            overviewsError={overviewsError}
            onAttentionNavigate={handleAttentionNavigate}
          />
        </>
      )}
    </div>
  );
};

export default ClientDashboardPage;
